import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    mismatchMessage: { type: String, default: 'Passwords do not match' },
    validClass: { type: String, default: '' },
    invalidClass: { type: String, default: 'text-muted' },
    validIconClass: { type: String, default: 'text-success' },
    invalidIconClass: { type: String, default: 'text-muted' }
  };
  static targets = [
    'currentPasswordInput',
    'newPasswordInput',
    'confirmPasswordInput',
    'confirmPasswordInputFeedback',
    'submitButton',
    'passwordValidation',
    'passwordLength',
    'passwordUppercase',
    'passwordLowercase',
    'passwordNumber',
    'passwordSymbol',
  ];

  initialize() {
    this.spinner = document.createElement('span');
    this.spinner.classList.add('spinner-border', 'spinner-border-sm', 'me-1');
    this.spinner.setAttribute('role', 'status');
  }

  connect() {
    // Handle initial state, e.g. when browser autofills password fields
    this.validatePassword(false);
  }

  togglePasswordVisibility(e) {
    const inputTarget = this[e.currentTarget.dataset.target + 'Target'];
    if (!inputTarget) return;

    const isPassword = inputTarget.type === 'password';
    inputTarget.type = isPassword ? 'text' : 'password';
    this.toggleAttributes(e, isPassword);
  }

  toggleAttributes(e, isPassword){
    const label = isPassword ? 'Hide password' : 'Show password';
    e.currentTarget.children[0].classList.toggle('fa-eye', !isPassword);
    e.currentTarget.children[0].classList.toggle('fa-eye-slash', isPassword);
    e.currentTarget.setAttribute('aria-label', label);
    e.currentTarget.setAttribute('title', label);
  }

  toggleCriteriaSuccess(element, success) {
    if (!element) return;

    const icon = element.querySelector('i');
    if (!icon) return;

    element.classList.toggle(this.invalidClassValue, !success);
    icon.classList.toggle(this.validIconClassValue, success);
    icon.classList.toggle('fa-circle', !success);
    icon.classList.toggle('fa-check-circle', success);
  }

  isValidPassword() {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/.test(this.newPasswordInputTarget?.value);
  }

  validatePassword(validateConfirmPassword = true) {
    const password = this.newPasswordInputTarget?.value;

    this.toggleCriteriaSuccess(this.passwordLengthTarget, password.length >= 8);
    this.toggleCriteriaSuccess(this.passwordUppercaseTarget, /[A-Z]/.test(password));
    this.toggleCriteriaSuccess(this.passwordLowercaseTarget, /[a-z]/.test(password));
    this.toggleCriteriaSuccess(this.passwordNumberTarget, /\d/.test(password));
    this.toggleCriteriaSuccess(this.passwordSymbolTarget, /[\W_]/.test(password));

    this.hasPasswordValidationTarget && this.passwordValidationTarget.classList.toggle('border-success', this.isValidPassword());

    validateConfirmPassword && this.validateConfirmPassword();
  }

  validateConfirmPassword() {
    const password = this.newPasswordInputTarget?.value;
    const confirmPassword = this.confirmPasswordInputTarget?.value;

    const passwordsNotMatching = confirmPassword && password !== confirmPassword;
    const invalidFeedback = passwordsNotMatching ? this.mismatchMessageValue : '';

    this.confirmPasswordInputTarget.classList.toggle('is-invalid', passwordsNotMatching);
    this.confirmPasswordInputTarget.setCustomValidity(invalidFeedback);
    this.confirmPasswordInputFeedbackTarget.textContent = invalidFeedback;

    this.submitButtonTarget.toggleAttribute('disabled', passwordsNotMatching || !this.isValidPassword());
  }

  validateCurrentPassword() {
    if (this.hasCurrentPasswordInputTarget) {
      this.submitButtonTarget.toggleAttribute('disabled', !this.currentPasswordInputTarget.value);
    }
  }

  submit() {
    if (this.isValidPassword() && this.confirmPasswordInputTarget.value
      && ((this.hasCurrentPasswordInputTarget && this.currentPasswordInputTarget.value) || !this.hasCurrentPasswordInputTarget)
    ) {
      this.submitButtonTarget.setAttribute('disabled', true);

      if (this.submitButtonTarget.classList.contains('button-turbo')) {
        this.submitButtonTarget.setAttribute('aria-busy', 'true');
      } else {
        this.submitButtonTarget.prepend(this.spinner);
      }
    }

    this.element.requestSubmit();
  }
}
